// Helpers d'affichage member_type.
//
// IMPORTANT : la source de vérité métier reste le BACKEND
// (backend/config/memberTypeConfig.js, servi via /me/entitlements →
// memberTypeConfig.labels/available/default/badgeColors/selfDeclarableTypes).
// Ce fichier ne traduit QUE l'affichage des labels, sans toucher à la logique.
//
// Deux familles de labels :
//   1. getMemberTypeLabel(orgType, type, t, fallbackLabels)
//      → libellé métier précis, variant par org_type (fiche, modal admin,
//        verify-profile). Clé i18n : business:member_type.<orgType>.<type>.
//        Fallback = label backend (memberTypeConfig.labels), puis le type brut.
//   2. getMemberTypeShortLabel(type, t)
//      → libellé court générique (org_type-agnostic) pour les filtres Map
//        (dropdown + chips). Clé i18n : business:member_type_short.<type>.
//
// Si tu ajoutes un type/org_type : propage backend/config/memberTypeConfig.js
// + business.json (member_type + member_type_short).

// Fallback FR court (data-safe) : utilisé hors React ou si `t` absent.
// Ex-duplication MEMBER_TYPE_LABELS de MapFilterBar.js + useMapFilters.js,
// centralisée ici.
export const MEMBER_TYPE_SHORT_LABELS = {
  student: 'Étudiant',
  alumni: 'Alumni',
  member: 'Membre',
  external: 'Externe',
  professor: 'Enseignant(e)',
}

// Label métier précis d'un member_type, variant par org_type.
// `fallbackLabels` = memberTypeConfig.labels (backend FR) — filet data-safe si
// la clé i18n n'existe pas (org_type/type inconnu côté front).
export const getMemberTypeLabel = (orgType, type, t, fallbackLabels) => {
  if (!type) return ''
  const backendFallback = (fallbackLabels && fallbackLabels[type]) || type
  if (t) {
    return t(`business:member_type.${orgType || 'default'}.${type}`, {
      defaultValue: backendFallback,
    })
  }
  return backendFallback
}

// Label court générique (filtres Map). `t` optionnel → fallback FR court.
export const getMemberTypeShortLabel = (type, t) => {
  if (!type) return ''
  if (t) return t(`business:member_type_short.${type}`, { defaultValue: MEMBER_TYPE_SHORT_LABELS[type] || type })
  return MEMBER_TYPE_SHORT_LABELS[type] || type
}

// ── Filtre annuaire par type de membre ──────────────────────────────────────
//
// `member_type` n'est PAS dans PRIVACY_FIELDS côté backend
// (backend/utils/memberPrivacy.js) : il est donc présent dans chaque ligne de
// `GET /members`. Le filtre est purement client-side, sans second aller-retour,
// exactement comme `sector` et `intervention_roles`.
//
// Les deux fonctions ci-dessous sont PURES et sans dépendance React : c'est ce
// qui les rend testables sous vitest (environnement `node`, pas de DOM).

/**
 * Options du filtre annuaire, dans l'ordre de la config backend.
 *
 * Utilise `available` et NON `selfDeclarableTypes` : on doit pouvoir FILTRER
 * sur un type admin-only (professor) même si personne ne peut l'auto-déclarer.
 * Même arbitrage que backend/services/postTargetingOptionsService.js.
 *
 * Retourne [] s'il y a moins de 2 types : proposer un filtre à un seul choix
 * n'apporte rien et encombre la barre de facettes (cas des org_types à 2
 * valeurs dont une seule est réellement peuplée).
 *
 * @param {object|null} memberTypeConfig - depuis EntitlementsContext
 * @param {string|null} orgType
 * @param {function} [t] - i18next
 * @returns {{value: string, label: string}[]}
 */
export const buildMemberTypeFilterOptions = (memberTypeConfig, orgType, t) => {
  const available = memberTypeConfig?.available
  if (!Array.isArray(available) || available.length < 2) return []
  return available.map((type) => ({
    value: type,
    label: getMemberTypeLabel(orgType, type, t, memberTypeConfig?.labels),
  }))
}

/**
 * Prédicat du filtre : OR sur les types sélectionnés.
 *
 * Sélection vide (ou non-tableau) = pas de filtre actif → tout passe. C'est
 * l'invariant à ne pas casser : un filtre non renseigné ne doit JAMAIS vider
 * l'annuaire.
 *
 * @param {object|null} member - ligne de GET /members
 * @param {string[]} selectedTypes
 * @returns {boolean}
 */
export const matchesMemberTypeFilter = (member, selectedTypes) => {
  if (!Array.isArray(selectedTypes) || selectedTypes.length === 0) return true
  if (!member) return false
  return selectedTypes.includes(member.member_type)
}
